import { CircularProgress, Dialog, Switch } from '@material-ui/core';
import { FormikHelpers, useFormik } from 'formik';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import en from '../../lang/en-us';
import Form, { InputType } from '../../lib/Formik';
import {
    ONE_SPECIAL_CHARACTER,
    PasswordGuidelines,
    UPPER_CASE_LOWER_CASE,
    WHITESPACE,
} from '../../lib/PasswordGuidelines';
import {
    onChangePassword,
    onLogout,
    updateFirstTimeSettings,
} from '../../redux-store/actions/login';
import { RootStateType } from '../../redux-store/reducers';
import { ChangePasswordPayload } from '../../redux-store/types/login';
import { giveFeedback, onSaveDisplayName } from '../../redux-store/actions/nct';
import * as yup from 'yup';
import { NoteModals } from '../../lib/NoteModals';
import GiveFeedbackUI from './giveFeedBack';
import { SubmitButton } from '../../lib/Button';
import { checkForOnlyAlphabets, isMobileDevice, OTP_ACTIONS } from '../../utils/constants';
import TextInput from '../../lib/TextField';
import { isFingerPrintAvailable } from '../../lib/Authentication';
import { maskEmail, maskMobileNumber, maskPan } from '../../utils/utilityFunctions';
const { myProfile, changePassword, common, dashboard, passwordGuidelines } = en;

interface ChangePasswordProps {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

type feedbackpayload = {
    userName: string;
    userEmailId: string;
    queryType: string;
    query: string;
    queryReferenceId: string;
};

function MyProfile({ displayName = '', pan = 'N/A', mobile = 'N/A', ...props }: any) {
    const [open, setOpen] = React.useState(false);
    const [showModal, setModal] = React.useState(false);
    const [successModal, setSuccessModal] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [isDisplayNameEditing, setIsDisplayNameEditing] = React.useState(false);
    const [displayNameValue, setDisplayNameValue] = React.useState(displayName);
    const [isDisplayNameChangeLoading, setIsDisplayNameChangeLoading] = React.useState(false);
    const [successDisplayNameModal, setSuccessDisplayNameModal] = React.useState(false);
    const [appVersion, setAppVersion] = React.useState(null);

    const handleClickOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        formik.setErrors({});
    };
    const [feedbackOpen, setfeedbackOpen] = React.useState(false);

    const feedbackHandleClickOpen = () => {
        setfeedbackOpen(true);
    };
    const feedbackHandleClose = () => {
        setfeedbackOpen(false);
        formik.setErrors({});
    };
    const inputFields: Partial<InputType>[] = [
        {
            type: 'password',
            name: 'currentPassword',
            label: changePassword.oldPassword,
        },
        {
            type: 'password',
            name: 'newPassword',
            label: changePassword.setNewPassword,
        },
        {
            label: changePassword.confirmPassword,
            name: 'confirmPassword',
            type: 'password',
        },
        {
            component: PasswordGuidelines,
            name: 'newPassword',
        },
    ];

    const initialValues: ChangePasswordProps = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    };

    const handleSave = async (
        values: ChangePasswordProps,
        action: FormikHelpers<ChangePasswordProps>,
    ) => {
        action.setSubmitting(true);
        const reqBody: ChangePasswordPayload = {
            pan,
            pekrn: props.pekrn,
            mobile,
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
            action: OTP_ACTIONS.CHANGE_PWD,
        };
        try {
            await props.onChangePassword(reqBody);
            setOpen(false);
            setModal(true);
            action.setSubmitting(false);
        } catch (err: any) {
            alert(err.message);
        }
    };

    const _handleModalClose = () => {
        const { pekrn } = props;
        const payload = {
            pan,
            pekrn,
            mobile,
        };
        props.onLogout(payload);
    };

    const validationSchema = yup.object({
        currentPassword: yup.string().required(common.errors.password.oldPasswordRequired),
        newPassword: yup
            .string()
            .required(common.errors.password.setPassword)
            .min(8, passwordGuidelines.one)
            .test(
                passwordGuidelines.one,
                passwordGuidelines.one,
                (value = '') => !WHITESPACE.test(value),
            )
            .matches(UPPER_CASE_LOWER_CASE, passwordGuidelines.two)
            .matches(ONE_SPECIAL_CHARACTER, passwordGuidelines.three),
        confirmPassword: yup.string().required(common.errors.password.confirmPasswordRequired),
    });

    const validate = (values: ChangePasswordProps) => {
        if (values.newPassword !== values.confirmPassword) {
            return {
                confirmPassword: common.errors.password.confirmPasswordError,
            };
        }
    };

    const formik = useFormik({
        initialValues,
        onSubmit: handleSave,
        validationSchema,
        validate,
    });

    const _submitFeedback = async (feedbackDetails: feedbackpayload) => {
        try {
            setSubmitting(true);
            const { pekrn, reqId } = props;
            await props.giveFeedback({
                pan,
                pekrn,
                reqId,
                mobile,
                ...feedbackDetails,
            });
            setfeedbackOpen(false);
            setSuccessModal(true);
        } catch (e: any) {
            alert(e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleChange = (value: string) => {
        setDisplayNameValue(value);
    };

    const handleSaveDisplayName = async () => {
        const { pekrn } = props;
        const payload = {
            pan,
            pekrn,
            mobile,
            newDisplayName: displayNameValue.trim(),
            action: OTP_ACTIONS.CHANGE_DISPLAY_NAME,
        };
        if (displayNameValue === '') {
            return;
        } else if (displayNameValue === displayName) {
            setIsDisplayNameEditing(false);
            return;
        } else {
            setIsDisplayNameChangeLoading(true);
            try {
                await props.onSaveDisplayName(payload);
                setIsDisplayNameEditing(false);
                setSuccessDisplayNameModal(true);
            } catch (e: any) {
                alert(e.message);
            } finally {
                setIsDisplayNameChangeLoading(false);
                setDisplayNameValue((prev: string) => prev.trim());
            }
        }
    };

    const handleAuthentication = () => {
        isFingerPrintAvailable(
            () => {
                props.updateFirstTimeSettings({ enableTwoFactorAuth: !props.enableTwoFactorAuth });
            },

            (error: any) => alert(error.message),
        );
    };

    useEffect(() => {
        const getAppVersion = async () => {
            const _window: any = window;
            const currentAppVersion = await _window.cordova.getAppVersion.getVersionNumber();
            setAppVersion(currentAppVersion);
        };
        if (isMobileDevice()) {
            getAppVersion();
        }
    }, []);

    return (
        <div className="curved-bg profile-page">
            <h2 className="heading">{myProfile.heading}</h2>
            <div className="profile-page-content">
                <div className="profile-page-left">
                    <div className="profile-left-sections profile-top-info">
                        <h4>{myProfile.accountInfo}</h4>
                        <div className="profile-page-info">
                            <div className="heading">
                                <img src="/images/user.svg" alt="" />
                                <h5>{myProfile.userName}</h5>
                            </div>
                            <div className="value">
                                {isDisplayNameEditing ? (
                                    <TextInput
                                        id="update-display-name"
                                        size="small"
                                        className="update-display-name"
                                        variant="outlined"
                                        name={'userDisplayName'}
                                        value={displayNameValue}
                                        onChange={({ target: { value } }) => {
                                            if (checkForOnlyAlphabets(value)) {
                                                handleChange(value);
                                            }
                                        }}
                                        error={displayNameValue === ''}
                                        helperText={
                                            displayNameValue === '' ? "Can't be empty!" : null
                                        }
                                    />
                                ) : (
                                    <p>{displayNameValue}</p>
                                )}
                                {isDisplayNameEditing ? (
                                    isDisplayNameChangeLoading ? (
                                        <CircularProgress size={36} />
                                    ) : (
                                        <>
                                            <a role="button" onClick={handleSaveDisplayName}>
                                                <img
                                                    src="/images/green-tick.svg"
                                                    alt=""
                                                    width={20}
                                                />
                                            </a>
                                            <a
                                                role="button"
                                                onClick={() => {
                                                    setDisplayNameValue(displayName);
                                                    setIsDisplayNameEditing(false);
                                                }}
                                            >
                                                <img
                                                    src="/images/close-red.svg"
                                                    alt=""
                                                    width={20}
                                                />
                                            </a>
                                        </>
                                    )
                                ) : (
                                    <a
                                        role="button"
                                        onClick={() => {
                                            setIsDisplayNameEditing(true);
                                        }}
                                    >
                                        <img src="/images/edit-purple.svg" alt="" />
                                    </a>
                                )}
                            </div>
                        </div>
                        <div className="profile-page-info">
                            <div className="heading">
                                <img src="/images/phone.svg" alt="" />
                                <h5>
                                    {props.userRegisteredWith === 'EMAIL'
                                        ? myProfile.emailId
                                        : myProfile.mobileNo}
                                </h5>
                            </div>
                            <div className="value">
                                <p>
                                    {props.userRegisteredWith === 'EMAIL'
                                        ? maskEmail(props.email)
                                        : maskMobileNumber(props.displayMobile)}
                                </p>
                                {/* <a href="#">
                                    <img src="/images/edit-purple.svg" alt="" />
                                </a> */}
                            </div>
                        </div>
                        <div className="profile-page-info">
                            <div className="heading">
                                <img src="/images/profile-pan.svg" alt="" />
                                <h5>{myProfile.panNoOrPekrn}</h5>
                            </div>
                            <div className="value">
                                <p>{maskPan(pan || props.pekrn)}</p>
                            </div>
                        </div>
                    </div>
                    <div className="profile-left-sections profile-security">
                        <h4>{myProfile.security}</h4>
                        <div className="profile-page-info">
                            <div
                                className="heading"
                                style={{
                                    maxWidth: '100%',
                                }}
                            >
                                <img src="/images/lock.svg" alt="" />
                                <a href="#" onClick={handleClickOpen}>
                                    <h5>{myProfile.changePassword}</h5>
                                </a>
                            </div>
                        </div>
                        <div className="profile-page-info">
                            <div
                                className="heading"
                                style={{
                                    maxWidth: '100%',
                                }}
                            >
                                <img src="/images/shield.svg" alt="" />
                                <a onClick={() => props.history.push('update-security-questions')}>
                                    <h5>{myProfile.changeSecurity}</h5>
                                </a>
                            </div>
                        </div>
                        {isMobileDevice() && (
                            <div className="profile-page-info">
                                <div
                                    className="heading"
                                    style={{
                                        maxWidth: '100%',
                                    }}
                                >
                                    <img src="/images/shield.svg" alt="" />
                                    <a>
                                        <h5>{myProfile.enableFingerPrint}</h5>
                                    </a>
                                </div>
                                <Switch
                                    onChange={handleAuthentication}
                                    checked={!!props.enableTwoFactorAuth}
                                />
                            </div>
                        )}
                        {isMobileDevice() && (
                            <div className="profile-page-info">
                                <div
                                    className="heading"
                                    style={{
                                        maxWidth: '100%',
                                    }}
                                >
                                    <img src="/images/shield.svg" alt="" />
                                    <a onClick={() => props.history.push('/stack/reset-pin')}>
                                        <h5>{'Reset/Change Device PIN'}</h5>
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                    {/* <div className="profile-left-sections profile-communications">
                        <h4>{myProfile.communications}</h4>
                        <div className="profile-page-info">
                            <div className="heading">
                                <img src="/images/bell-small.svg" alt="" />
                                <h5>{myProfile.pushNotifications}</h5>
                            </div>
                            <div className="value">
                                <Switch
                                    name="push-notifications"
                                    inputProps={{ 'aria-label': 'push-notifications' }}
                                />
                            </div>
                        </div>
                    </div> */}
                </div>
                <hr className="profile-mobile-separator" />
                <div className="profile-page-right">
                    <h4 className="contact-us-title">{myProfile.contactUs}</h4>
                    <div className="heading">
                        <img src="/images/mail.svg" alt="icon" />
                        <a
                            href="mailto:support@mfcentral.com"
                            style={{ textDecoration: 'underline' }}
                        >
                            support@mfcentral.com
                        </a>
                    </div>
                    <div className="heading">
                        <img src="/images/give-feedback.svg" alt="" />
                        <a onClick={feedbackHandleClickOpen}>{myProfile.giveFeedback}</a>
                    </div>
                    {/* <div className="heading">
                        <img src="/images/register-complaint.svg" alt="" />
                        <a>{myProfile.registerComplaint}</a>
                    </div> */}
                    <img className="profile-right-bg" src="/images/profile-right-bg.png" alt="" />
                </div>
                {isMobileDevice() && (
                    <p style={{ textAlign: 'right', fontSize: 12 }}>Version : {appVersion}</p>
                )}
            </div>
            <Dialog
                maxWidth={'sm'}
                onClose={handleClose}
                open={open}
                aria-labelledby="change-password-modal"
            >
                <div className="change-password-modal">
                    <h3>
                        {changePassword.heading}
                        {changePassword.headingBold}
                        <a onClick={handleClose}>
                            <img src="/images/close-black.svg" alt="" />
                        </a>
                    </h3>
                    <Form
                        ui={inputFields}
                        onCancel={handleClose}
                        submitButtonLabel={'Change'}
                        formik={formik}
                    ></Form>
                </div>
            </Dialog>
            <NoteModals
                showModal={showModal}
                heading={dashboard.Success.success}
                info={dashboard.noteModals.changePassword}
                proceedBtn={'Okay'}
                onProceed={_handleModalClose}
            />
            <Dialog
                maxWidth={'sm'}
                onClose={feedbackHandleClose}
                open={feedbackOpen}
                aria-labelledby="give-feedback-modal"
            >
                <div className="give-feedback-modal">
                    <h3>
                        {myProfile.giveFeedback}
                        <a onClick={feedbackHandleClose}>
                            <img src="/images/close-black.svg" alt="" />
                        </a>
                    </h3>
                    <GiveFeedbackUI
                        getDetails={(feedbackDetails: feedbackpayload) => {
                            _submitFeedback(feedbackDetails);
                        }}
                        isSubmitting={submitting}
                    />
                </div>
            </Dialog>
            <Dialog maxWidth={'sm'} open={successModal} aria-labelledby="feedback-success-modal">
                <div className="feedback-success-modal">
                    <img src="/images/green-tick.svg" alt="" />
                    <h4>{dashboard.noteModals.feedbackSubmitted}</h4>
                    <SubmitButton
                        onClick={() => setSuccessModal(false)}
                        type="submit"
                        label={dashboard.noteModals.okayTxt}
                    />
                </div>
            </Dialog>
            <Dialog
                maxWidth={'sm'}
                open={successDisplayNameModal}
                aria-labelledby="feedback-success-modal"
            >
                <div className="feedback-success-modal">
                    <img src="/images/green-tick.svg" alt="" />
                    <h4>{dashboard.noteModals.displayNameChanged}</h4>
                    <SubmitButton
                        onClick={() => setSuccessDisplayNameModal(false)}
                        type="submit"
                        label={dashboard.noteModals.okayTxt}
                    />
                </div>
            </Dialog>
        </div>
    );
}

export default connect(({ authReducer }: RootStateType) => ({ ...authReducer }), {
    onChangePassword,
    onLogout,
    giveFeedback,
    onSaveDisplayName,
    updateFirstTimeSettings,
})(MyProfile);
